import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const SimulationOverlay = ({ data, onClose }) => {
    const [hoveredActivation, setHoveredActivation] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    if (!data) return null;

    const handleMouseMove = (e) => {
        setMousePos({ x: e.clientX, y: e.clientY });
    };

    // Helper to render a table row
    const Row = ({ activation, prevEnd }) => {
        const isConflict = activation.is_conflict;
        // Simple overlap check for visual emphasis (not perfect logic but good for display)
        // const isOverlap = activation.start_time < prevEnd; // This line is removed as per the diff

        return (
            <tr
                className={`border-b border-gray-100 transition-colors cursor-help ${isConflict ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                onMouseEnter={() => setHoveredActivation(activation)}
                onMouseLeave={() => setHoveredActivation(null)}
                onMouseMove={handleMouseMove}
            >
                <td className="py-2 px-3 text-xs font-mono text-gray-500">{activation.id}</td>
                <td className="py-2 px-3 text-xs font-mono text-gray-800">{activation.start_time.toFixed(2)}s</td>
                <td className="py-2 px-3 text-xs font-mono text-gray-500">{activation.duration.toFixed(2)}s</td>
                <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isConflict
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                        {isConflict ? 'CONFLICT' : 'OPTIMAL'}
                    </span>
                </td>
            </tr>
        );
    };

    // Get first scenario for demo purposes (or could aggregate)
    const baselineScenario = data.baseline[0];
    const ncfScenario = data.ncf[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="absolute inset-0 z-50 flex items-center justify-center p-4 md:p-10 pointer-events-none"
        >
            {/* Tooltip */}
            <AnimatePresence>
                {hoveredActivation && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        style={{
                            top: mousePos.y + 15,
                            left: mousePos.x + 15,
                            position: 'fixed',
                            zIndex: 100
                        }}
                        className={`pointer-events-none p-4 rounded-xl shadow-2xl border backdrop-blur-md w-80 ${hoveredActivation.is_conflict
                                ? 'bg-red-900/90 border-red-500 text-white'
                                : 'bg-gray-900/90 border-gray-700 text-white'
                            }`}
                    >
                        <h4 className="font-bold text-sm mb-1 flex items-center gap-2">
                            {hoveredActivation.is_conflict ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            )}
                            {hoveredActivation.is_conflict ? 'Conflict Detected' : 'Activation Status'}
                        </h4>
                        <p className="text-xs opacity-90 mb-2">
                            ID: <span className="font-mono">{hoveredActivation.id}</span>
                        </p>
                        <div className="bg-black/20 p-2 rounded text-xs">
                            <span className="block text-[10px] uppercase tracking-wider opacity-70 mb-1">Analysis (INFEROBIA)</span>
                            <p className="font-medium leading-relaxed">
                                {hoveredActivation.reason || "No specific data available."}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="bg-white/95 backdrop-blur-xl w-full max-w-6xl h-[85vh] rounded-3xl shadow-2xl border border-white/50 flex flex-col pointer-events-auto overflow-hidden">

                {/* Header */}
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h2 className="text-2xl font-black text-gray-800 tracking-tight">ASANA Sequencing Analysis</h2>
                        <p className="text-sm text-gray-500">Comparative analysis of ANF activation schedules (Scenario 1)</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-500">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Content Grid */}
                <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">

                    {/* Baseline Column */}
                    <div className="flex flex-col overflow-hidden bg-red-50/10">
                        <div className="p-4 bg-red-50 border-b border-red-100">
                            <h3 className="font-bold text-red-900 flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                Baseline (No NCF)
                            </h3>
                            <div className="flex gap-4 mt-2 text-xs text-red-700">
                                <span>Conflicts: <strong>{baselineScenario.conflict_count}</strong></span>
                                <span>Rate: <strong>{((baselineScenario.conflict_count / 100) * 100).toFixed(1)}%</strong></span>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">ID</th>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">Start</th>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">Dur</th>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {baselineScenario.activations.map((act, i) => (
                                        <Row
                                            key={act.id}
                                            activation={act}
                                            prevEnd={i > 0 ? baselineScenario.activations[i - 1].start_time + baselineScenario.activations[i - 1].duration : 0}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* NCF Column */}
                    <div className="flex flex-col overflow-hidden bg-green-50/10">
                        <div className="p-4 bg-green-50 border-b border-green-100">
                            <h3 className="font-bold text-green-900 flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                With NCF (ASANA)
                            </h3>
                            <div className="flex gap-4 mt-2 text-xs text-green-700">
                                <span>Conflicts: <strong>{ncfScenario.conflict_count}</strong></span>
                                <span>Rate: <strong>{((ncfScenario.conflict_count / 100) * 100).toFixed(1)}%</strong></span>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">ID</th>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">Start</th>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">Dur</th>
                                        <th className="py-2 px-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ncfScenario.activations.map((act, i) => (
                                        <Row
                                            key={act.id}
                                            activation={act}
                                            prevEnd={i > 0 ? ncfScenario.activations[i - 1].start_time + ncfScenario.activations[i - 1].duration : 0}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
        </motion.div>
    );
};
